import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import { getApplicantsByJobId, updateApplicantStatus } from "../utils/methods";
import { strings } from "../utils";
import { Button } from "../components";
import CloseIcon from "../components/Icons/closeIcon";
import useFetchOptions from "../hooks/useFetchOptions";

const ViewApplicants = () => {
  const { id: careerId } = useParams();
  const location = useLocation();
  const { title } = location.state || {};
  const [applicants, setApplicants] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentApplicant, setCurrentApplicant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editStatusId, setEditStatusId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("All");
  const { applicantStatus } = useFetchOptions();
  const [currentPage, setCurrentPage] = useState(1);

  const usersPerPage = 20;
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(applicants.length / usersPerPage);

  const desiredStatusSequence = applicantStatus;

  const isIntern = title?.toLowerCase().includes("intern");

  useEffect(() => {
    const fetchApplicants = async () => {
      try {
        setLoading(true);
        const applicantData = await getApplicantsByJobId(careerId);

        const sortedApplicants = applicantData.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });

        setApplicants(sortedApplicants || []);
      } catch (error) {
        console.error("Error fetching applicants:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplicants();
  }, [careerId]);

  const handleClickOpen = (applicant) => {
    setCurrentApplicant(applicant);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentApplicant(null);
  };

  const handleStatusChange = async (event, applicantId) => {
    const newStatus = event.target.value;

    try {
      await updateApplicantStatus(applicantId, newStatus);

      setApplicants((prevApplicants) =>
        prevApplicants.map((applicant) =>
          applicant.id === applicantId
            ? { ...applicant, status: newStatus }
            : applicant
        )
      );

      setEditStatusId(null);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const filteredApplicants =
    filterStatus === "All"
      ? applicants
      : applicants.filter((applicant) =>
          filterStatus === "Select"
            ? !applicant.status || applicant.status === "Select"
            : applicant.status === filterStatus
        );

  const paginatedApplicants = filteredApplicants.slice(
    indexOfFirstUser,
    indexOfLastUser
  );

  const totalFilteredApplicants = filteredApplicants.length;

  return (
    <div className="container py-8 px-[22px] h-full overflow-scroll bg-border">
      <div className="flex justify-between items-center pb-6">
        <p className="text-[32px] font-semibold flex-row">
          {strings["applicantsTitle"]}
          {": "} {totalFilteredApplicants}
        </p>

        <FormControl sx={{ minWidth: 200 }}>
          <Select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
          >
            <MenuItem value="All">All</MenuItem>
            {desiredStatusSequence.map((status, index) => (
              <MenuItem key={index} value={status}>
                {status === "NextRound" ? "Shortlisted" : status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === 1 ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Previous
        </button>

        <span className="px-4 py-2">{`Page ${currentPage} of ${totalPages}`}</span>
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Next
        </button>
      </div>

      <TableContainer component={Paper} sx={{ border: "2px solid border" }}>
        <Table sx={{ minWidth: 650 }} aria-label="applicants table">
          <TableHead>
            <TableRow>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Email</TableCell>
              <TableCell align="center">Whatsapp</TableCell>
              <TableCell align="center">Experience</TableCell>
              <TableCell align="center">City</TableCell>
              {isIntern ? (
                <>
                  <TableCell align="center">CGPA</TableCell>
                  <TableCell align="center">Last Degree</TableCell>
                  <TableCell align="center">Completion Year</TableCell>
                </>
              ) : (
                <>
                  <TableCell align="center">Current Salary</TableCell>
                  <TableCell align="center">Expected Salary</TableCell>
                  <TableCell align="center">Joining Availability </TableCell>
                </>
              )}
              <TableCell align="center">Cover</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedApplicants.length > 0 ? (
              paginatedApplicants.map((applicant) => (
                <TableRow key={applicant.id}>
                  <TableCell align="center">{applicant.name}</TableCell>
                  <TableCell align="center">{applicant.email}</TableCell>
                  <TableCell align="center">
                    {applicant.whatsappNumber}
                  </TableCell>
                  <TableCell align="center">
                    {applicant.totalExperience}
                  </TableCell>

                  <TableCell align="center">{applicant.city}</TableCell>
                  {isIntern ? (
                    <>
                      <TableCell align="center">{applicant.cgpa}</TableCell>
                      <TableCell align="center">
                        {applicant.lastDegree}
                      </TableCell>
                      <TableCell align="center">
                        {applicant.lastDegreeCompletionYear}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="center">
                        {applicant.currentSalary}
                      </TableCell>
                      <TableCell align="center">
                        {applicant.expectedSalary}
                      </TableCell>
                      <TableCell align="center">
                        {applicant.expectedJoin}
                      </TableCell>
                    </>
                  )}

                  <TableCell align="center">
                    {applicant.coverLetter && (
                      <Button
                        variant="underlineBold"
                        onClick={() => handleClickOpen(applicant)}
                      >
                        {strings["detailBtn"]}
                      </Button>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {editStatusId === applicant.id ? (
                      <FormControl
                        sx={{ height: 30, backgroundColor: "white" }}
                      >
                        <Select
                          sx={{ height: 30 }}
                          value={applicant.status || "Select"}
                          onChange={(e) => handleStatusChange(e, applicant.id)}
                          onBlur={() => setEditStatusId(null)}
                        >
                          <MenuItem value="Select">Select</MenuItem>
                          {desiredStatusSequence.map((status, index) => (
                            <MenuItem key={index} value={status}>
                              {status === "NextRound" ? "Shortlisted" : status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <span
                        onClick={() => setEditStatusId(applicant.id)}
                        className={`inline-flex items-center justify-center px-3 py-1 rounded-full cursor-pointer text-sm font-medium whitespace-nowrap ${
                          applicant.status === "Rejected"
                            ? "bg-red-500 text-white"
                            : applicant.status === "NextRound"
                            ? "bg-green-500 text-white"
                            : "bg-gray-300"
                        }`}
                      >
                        {applicant.status === "NextRound"
                          ? "Shortlisted"
                          : applicant.status || "Select"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <a
                      href={applicant.cvLink}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button>{strings["downloadBtn"]}</Button>
                    </a>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No record found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          <div className="flex justify-between items-center">
            <p className="text-lg font-semibold">{strings["coverLetter"]}</p>
            <Button variant="noStyle" onClick={handleClose}>
              <CloseIcon />
            </Button>
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {currentApplicant && <p>{currentApplicant.coverLetter}</p>}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ViewApplicants;
